import { FRESH_AGE_DAYS, MIN_IMAGE_COUNT } from "../ebayListings/listedTable/listingScore";
import { IQueueRowSignals } from "./queueRowSignals";

export interface IDrawerSectionDone {
  grade: boolean;
  title: boolean;
  partNumbers: boolean;
  price: boolean;
  policies: boolean;
  health: boolean;
}

export interface IDrawerSectionInputs {
  signals: IQueueRowSignals;
  grade: string | null | undefined;
  /** The new title, whether already saved against the listing or still only in the drawer; null when unchanged. */
  stagedTitle: string | null;
  /** The new price on the same terms. */
  stagedPrice: number | null;
  /** Whether the PartPro lookup has run for this part - the only thing that fills the OE number. */
  partProLookupRun?: boolean;
  /** The operator has looked at the title and clicked it done, whether or not they changed it. */
  titleReviewed?: boolean;
  /** Shipping, payment and returns are set on the listing or staged. */
  policiesComplete?: boolean;
}

/** Tailwind for a drawer section that is done - the light green wash that says "nothing left here". */
export const DONE_SECTION_CLASSES = "border-green-200 bg-green-50";

/**
 * Which drawer sections are finished for this listing, so each can turn green as the operator works
 * down the drawer. Grade: one is set. Title: a new title is staged, or the operator has marked the title
 * reviewed as it stands. Part numbers: the part carries at least one real number (a WILM filler or
 * "Not Available" does not count) - the health checklist still asks for the second. Price: a new price
 * is staged, or the live price already sits within 10% of the market. Policies: shipping, payment and
 * returns are set on the listing or staged. Listing health: nothing fixable is missing (a missing OE
 * number is never held against the listing) and nothing blocks the relist.
 */
export function drawerSectionDone({
  signals,
  grade,
  stagedTitle,
  stagedPrice,
  titleReviewed = false,
  policiesComplete = false,
}: IDrawerSectionInputs): IDrawerSectionDone {
  return {
    grade: (grade ?? "").trim().length > 0,
    title: (stagedTitle != null && stagedTitle.trim().length > 0) || titleReviewed,
    partNumbers: signals.hasPartNumbers,
    price: (stagedPrice != null && stagedPrice > 0) || signals.delta.tone === "within",
    policies: policiesComplete,
    // A missing OE number is never held against the listing: nobody can type it in.
    health: !signals.relistBlocked && signals.score.missing.filter((key) => key !== "OEMPN").length === 0,
  };
}

export interface IDrawerTodo {
  key: "blocked" | "fields" | "photos" | "grade" | "title" | "price" | "relist";
  label: string;
  done: boolean;
}

/** The score keys as the to-do list names them. The OE number is left out: nobody types it in. */
const FIELD_LABELS: Record<string, string> = {
  MPN: "MPN",
  KType: "KType",
  Brand: "Brand",
  Grade: "Grade",
  Make: "Make",
  Model: "Model",
  VIN: "VIN",
};

/**
 * Everything there is to do on this listing, in the order the drawer lays it out, each ticked once
 * it is done. Replaces the single "next step" pill, which only ever named the top-priority item and
 * so read as "add fields" on a listing whose title had not been looked at yet.
 */
export function drawerTodos(inputs: IDrawerSectionInputs): IDrawerTodo[] {
  const { signals } = inputs;
  const done = drawerSectionDone(inputs);
  const todos: IDrawerTodo[] = [];

  if (signals.relistBlocked) {
    todos.push({ key: "blocked", label: `Fix the listing: ${signals.relistErrors.join("; ") || "relist is blocked"}`, done: false });
  }

  todos.push({ key: "grade", label: done.grade ? "Grade set" : "Set a grade", done: done.grade });

  // Fields the operator can fill by hand. A missing OE number is not a job: the PartPro lookup
  // either finds one or it doesn't. With no MPN and no lookup yet, the lookup is the first thing to try.
  const typedFields = signals.score.missing.filter((key) => key !== "OEMPN").map((key) => FIELD_LABELS[key] ?? key);
  const offerLookup = signals.score.missing.includes("MPN") && inputs.partProLookupRun !== true;
  todos.push({
    key: "fields",
    label:
      typedFields.length > 0
        ? `Add missing fields: ${typedFields.join(", ")}${offerLookup ? " — or run the PartPro lookup" : ""}`
        : "Item specifics complete",
    done: typedFields.length === 0,
  });

  if (signals.imageCount != null) {
    const enough = signals.imageCount > MIN_IMAGE_COUNT;
    todos.push({
      key: "photos",
      label: enough ? `${signals.imageCount} photos` : `Add photos (${signals.imageCount} of ${MIN_IMAGE_COUNT + 1} eBay rewards)`,
      done: enough,
    });
  }

  todos.push({ key: "title", label: done.title ? "New title staged" : "Review the title", done: done.title });

  todos.push({
    key: "price",
    label: done.price
      ? inputs.stagedPrice != null && inputs.stagedPrice > 0
        ? "New price staged"
        : "Price within 10% of the market"
      : signals.delta.tone === "above" || signals.delta.tone === "below"
        ? `Reprice (${signals.delta.label})`
        : "Check the price",
    done: done.price,
  });

  const stale = signals.listingDays != null && signals.listingDays > FRESH_AGE_DAYS;
  todos.push({
    key: "relist",
    label: stale ? `Send to relist (listed ${signals.listingDays}d ago)` : "Listing is fresh",
    done: !stale,
  });

  return todos;
}
